if (!customElements.get('header-search-desktop-lock')) {
  customElements.define(
    'header-search-desktop-lock',
    class HeaderSearchDesktopLock extends HTMLElement {
      constructor() {
        super();

        this.mq = window.matchMedia('(min-width: 990px)');

        this.selectors = {
          details: 'details-modal.header__search > details, .header__search > details',
        };

        this.onToggleCapture = this.onToggleCapture.bind(this);
        this.onDocumentClickCapture = this.onDocumentClickCapture.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);
        this.syncByMq = this.syncByMq.bind(this);

        this.clickFallbackRaf = null;

        // Use theme's debounce() to avoid excessive work on resize/scrollbar changes.
        this.onResizeDebounced = debounce(() => this.syncByMq(), 100);
      }

      connectedCallback() {
        // Sync state on initial load.
        this.syncByMq();

        // Restore on section re-render (Theme Editor / dynamic updates).
        document.addEventListener('shopify:section:load', this.syncByMq);

        // Keep desktop search open if something closes the <details>.
        document.addEventListener('toggle', this.onToggleCapture, true);

        // Fallback: some themes close via outside click handlers.
        document.addEventListener('click', this.onDocumentClickCapture, true);

        // Breakpoint boundary crossed.
        if (this.mq.addEventListener) {
          this.mq.addEventListener('change', this.onMediaChange);
        } else {
          // Safari fallback
          this.mq.addListener(this.onMediaChange);
        }

        // Safari can change viewport behavior when scrollbar appears/disappears.
        // Resize is the most reliable universal signal to resync.
        window.addEventListener('resize', this.onResizeDebounced, { passive: true });
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.syncByMq);
        document.removeEventListener('toggle', this.onToggleCapture, true);
        document.removeEventListener('click', this.onDocumentClickCapture, true);

        if (this.mq.removeEventListener) {
          this.mq.removeEventListener('change', this.onMediaChange);
        } else {
          // Safari fallback
          this.mq.removeListener(this.onMediaChange);
        }

        window.removeEventListener('resize', this.onResizeDebounced);

        if (this.clickFallbackRaf) cancelAnimationFrame(this.clickFallbackRaf);
      }

      getHeaderSearchDetails() {
        return Array.from(document.querySelectorAll(this.selectors.details));
      }

      syncByMq() {
        if (this.mq.matches) {
          this.forceOpenDesktop();
        } else {
          this.releaseToMobile();
        }
      }

      forceOpenDesktop() {
        // Desktop mode: force <details open>.
        this.getHeaderSearchDetails().forEach((details) => {
          if (!details.hasAttribute('open')) details.setAttribute('open', '');

          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'true');
        });
      }

      releaseToMobile() {
        // Mobile mode: ensure <details> is closed so theme's toggle behavior works normally.
        if (this.clickFallbackRaf) {
          cancelAnimationFrame(this.clickFallbackRaf);
          this.clickFallbackRaf = null;
        }

        this.getHeaderSearchDetails().forEach((details) => {
          if (details.hasAttribute('open')) details.removeAttribute('open');

          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'false');
        });
      }

      onMediaChange() {
        this.syncByMq();
      }

      onToggleCapture(event) {
        if (!this.mq.matches) return;

        const details = event.target;
        if (!details || details.tagName !== 'DETAILS') return;
        if (!details.matches(this.selectors.details)) return;

        // If it was closed, reopen in desktop mode.
        if (!details.hasAttribute('open')) {
          details.setAttribute('open', '');
          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'true');
        }
      }

      onDocumentClickCapture() {
        if (!this.mq.matches) return;

        // Debounce to one operation per frame.
        if (this.clickFallbackRaf) return;

        this.clickFallbackRaf = requestAnimationFrame(() => {
          this.clickFallbackRaf = null;

          // Only enforce in desktop mode.
          if (this.mq.matches) this.forceOpenDesktop();
        });
      }
    }
  );
}